import { DateTime } from "luxon";
import User from "@/inside/models/User";
import Requirement from "@/inside/models/Text/Requirement";

/**
 * @property {string|null} id
 * @property {Requirement|null} requirement
 * @property {string} compliance
 * @property {DateTime|null} lastAssessmentDate
 * @property {User|null} lastAssessor
 * @property {number} nbActions
 * @property {number} nbComments
 * @property {string} relevance
 * @property {Assignment|null} assignment
 */
export default class Assessment {
    constructor() {
        this.id = null;
        this.requirement = null;
        this.compliance = Assessment.statusUnset;
        this.lastAssessmentDate = null;
        this.lastAssessor = null;
        this.nbActions = 0;
        this.nbComments = 0;
        this.relevance = Assessment.statusUnset;
        this.assignment = null;
    }

    /**
     * Returns a new `Assessment` instance hydrated with data from given `api_resource`.
     *
     * Please note that `Assessment.requirement` property **is not** hydrated by this method.
     * @param {object} api_resource
     * @return {Assessment}
     */
    static fromAPI(api_resource) {
        if (typeof api_resource !== "object") {
            throw new TypeError(`Assessment.fromAPI() method expects parameter 1 to be an object, ${typeof api_resource} given`);
        }

        let out = new Assessment();

        Object.assign(out, {
            id: api_resource.id,
            requirement: (api_resource.requirement !== null
                ? Requirement.fromAPI(api_resource.requirement)
                : null
            ),
            compliance: api_resource.compliance,
            nbActions: api_resource.nbActions,
            nbComments: api_resource.nbComments,
            relevance: api_resource.relevance
        });

        if (api_resource.lastAssessmentDate !== null) {
            // noinspection JSCheckFunctionSignatures
            out.lastAssessmentDate = DateTime.fromFormat(
                api_resource.lastAssessmentDate,
                "yyyy-MM-dd HH:mm:ss",
                {
                    zone: "utc"
                }
            ).setZone("local");
        }

        // noinspection JSUnresolvedVariable
        if (
            api_resource.lastAssessor !== null
            && Object.prototype.hasOwnProperty.call(api_resource.lastAssessor, "resource")
            && api_resource.lastAssessor.resource !== null
        ) {
            if (api_resource.lastAssessor.type === "team.member") {
                const user = new User();
                // noinspection JSUnresolvedVariable
                user.firstname = api_resource.lastAssessor.resource.firstname;
                // noinspection JSUnresolvedVariable
                user.lastname = api_resource.lastAssessor.resource.lastname;
                out.lastAssessor = user;
            } else {
                // noinspection JSUnresolvedVariable
                out.lastAssessor = User.fromAPI(api_resource.lastAssessor.resource);
            }
        }

        return out;
    }

    /**
     * Returns a boolean indicating whether this assessment is "complete".
     *
     * An assessment is "complete" if either:
     * - it's not relevant;
     * - it's relevant but text-base is not compliant;
     * - it's relevant and text-base is compliant.
     * @return {boolean}
     */
     get isComplete() {
        return (
            this.relevance === Assessment.statusNegative
        ) || (
            this.relevance === Assessment.statusPositive
            && [Assessment.statusNegative, Assessment.statusPositive].includes(this.compliance)
        );
    }

    /**
     * Returns a boolean indicating whether this assessment is linked to action.
     * @return {boolean}
     */
    get isLinked(){
        return this.nbActions > 0;
    }

    /**
     * Returns a boolean indicating whether this assessment is new (not saved to database yet).
     * @return {boolean}
     */
    get isNew(){
        return this.id === null;
    }

    /**
     * Returns a boolean indicating whether given `candidate` is a valid assessment status.
     * @param {string} candidate
     * @return {boolean}
     */
    static isValidStatus(candidate) {
        return [
            Assessment.statusNegative,
            Assessment.statusPositive,
            Assessment.statusUnset,
            Assessment.statusUnsure
        ].includes(candidate);
    }

    toJSON(){
        return {
            compliance: this.compliance,
            relevance: this.relevance,
        };
    }

    /**
     * @return {string}
     */
    static get statusNegative() {
        return "negative";
    }

    /**
     * @return {string}
     */
    static get statusPositive() {
        return "positive";
    }

    /**
     * @return {string}
     */
    static get statusUnset() {
        return "unset";
    }

    /**
     * @return {string}
     */
    static get statusUnsure() {
        return "unsure";
    }
}
